import { computed, inject, Injectable, signal } from '@angular/core';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { DEFAULT_PAGE_SIZE, PageMeta } from '@/app/core/models/paged-result.model';
import { PeriodIso } from '@/app/shared/utils/period.utils';
import {
  AvailablePeriod,
  BeneficiaryRecord,
  BeneficiaryRegistryFilters,
} from '../models/beneficiary-registry.model';
import { CATALOG_PAGE_SIZE, GroupOption, PlanOption } from '../models/catalog.model';
import { BeneficiaryRegistryRepository } from '../services/beneficiary-registry.repository';
import { RemoteCatalog } from './remote-catalog';

export type RegistryStatus = 'loading' | 'ready' | 'empty' | 'noRegistry' | 'error';

const emptyPage = (size: number): PageMeta => ({
  page: 0,
  size,
  totalElements: 0,
  totalPages: 1,
});

const ERROR_SUBJECT = 'el padrón';

@Injectable()
export class BeneficiaryRegistryStore {
  private readonly repository = inject(BeneficiaryRegistryRepository);

  private readonly _status = signal<RegistryStatus>('loading');
  private readonly _records = signal<readonly BeneficiaryRecord[]>([]);
  private readonly _pageMeta = signal<PageMeta>(emptyPage(DEFAULT_PAGE_SIZE));
  private readonly _period = signal<PeriodIso>('');
  private readonly _processDate = signal<string | null>(null);
  private readonly _filters = signal<BeneficiaryRegistryFilters>({});
  private readonly _availablePeriods = signal<readonly AvailablePeriod[]>([]);
  private catalogPeriod: PeriodIso = '';
  private readonly _error = signal<AppError | null>(null);

  readonly status = this._status.asReadonly();
  readonly error = this._error.asReadonly();
  readonly records = this._records.asReadonly();
  readonly pageMeta = this._pageMeta.asReadonly();
  readonly period = this._period.asReadonly();
  readonly processDate = this._processDate.asReadonly();
  readonly filters = this._filters.asReadonly();
  readonly availablePeriods = this._availablePeriods.asReadonly();
  readonly plans = new RemoteCatalog<PlanOption>((search) =>
    this.repository.getPlans({
      period: this.catalogPeriod || undefined,
      search,
      size: CATALOG_PAGE_SIZE,
    }),
  );
  readonly groups = new RemoteCatalog<GroupOption>((search) =>
    this.repository.getGroups({
      period: this.catalogPeriod || undefined,
      search,
      size: CATALOG_PAGE_SIZE,
    }),
  );

  readonly isLoading = computed(() => this._status() === 'loading');
  readonly resultCountLabel = computed(() => `${this._pageMeta().totalElements} beneficiario(s)`);
  readonly canInteract = computed(() => this._status() === 'ready');

  reset(): void {
    this._status.set('loading');
    this._records.set([]);
    this._pageMeta.set(emptyPage(DEFAULT_PAGE_SIZE));
    this._period.set('');
    this._processDate.set(null);
    this._filters.set({});
    this._availablePeriods.set([]);
    this.catalogPeriod = '';
    this.plans.reset();
    this.groups.reset();
    this._error.set(null);
  }

  loadPeriods(): void {
    this.repository.getAvailablePeriods().subscribe({
      next: (result) => this._availablePeriods.set(result.periods),
      error: () => this._availablePeriods.set([]),
    });
  }

  setCatalogPeriod(period: PeriodIso): void {
    if (period === this.catalogPeriod) return;
    this.catalogPeriod = period;
    this.plans.reset();
    this.groups.reset();
  }

  search(filters: BeneficiaryRegistryFilters): void {
    this._filters.set(filters);
    this.load(0);
  }

  load(page = 0, size = this._pageMeta().size): void {
    this._error.set(null);
    this._status.set('loading');

    this.repository.getRegistry({ page, size, ...this._filters() }).subscribe({
      next: (result) => {
        this._records.set(result.content);
        this._pageMeta.set(result.page);
        this._period.set(result.period);
        this._processDate.set(result.processDate ?? null);
        this._status.set(result.page.totalElements === 0 ? 'empty' : 'ready');
      },
      error: (cause: unknown) => {
        const appError = toAppError(cause, ERROR_SUBJECT);
        this._records.set([]);
        this._pageMeta.set(emptyPage(size));

        if (appError.kind === 'notFound') {
          this._status.set('noRegistry');
          return;
        }

        this._error.set(appError);
        this._status.set('error');
      },
    });
  }
}
